"use client";

import type { GamePrioritiesResponse } from "@/app/_lib/apis/gamePriorities";
import type { PredictedStatsResponse } from "@/domain/gems-game/domain/lib/Sweepstake";
import { useAccessToken } from "@/hooks";
import { useFlag } from "@/hooks/useFeatureFlags";
import { authorizedFetcherGet } from "@/utils/request";
import {
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import useSWR from "swr";
import type {
  CampaignQuestState,
  GameGroup,
  GameWithQuests,
} from "../QuestsV2/types";
import { IN_GAME_QUEST_TYPES, SOCIAL_QUEST_TYPES } from "../QuestsV2/types";
import { useQuestCompletions } from "../QuestsV2/useQuestCompletions";

// Constants
const RECURRING_PERIODS: Record<number, string> = {
  24: "Daily",
  168: "Weekly",
};

export const QUEST_TYPE_LABELS = {
  SOCIAL: "Social",
  IN_GAME: "In Game",
} as const;

export const REWARD_TYPES = {
  POINT: "point",
  WEEKLY_POINT: "weekly_point",
} as const;

// OS-based platform labels (multiple stores map to same OS)
const PLATFORM_TO_OS: Record<string, string> = {
  apple_app_store: "iOS",
  google_play: "Android",
  steam: "Windows",
  epic: "Windows",
  microsoft_store: "Windows",
  web_url: "Web",
  mac_app_store: "macOS",
} as const;

export function getPlatformLabel(platformKey: string): string {
  if (platformKey in PLATFORM_TO_OS) {
    return PLATFORM_TO_OS[platformKey];
  }
  return platformKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// TODO: actually use
const FEATURED_GAMES = ["OverKnights"];

export interface FilterOptions {
  gameTitle: string;
  platforms: Set<string>;
  rewards: Set<string>;
  recurring: Set<string>;
  questTypes: Set<string>;
  genres: Set<string>;
  hideCompleted: boolean;
}

interface QuestFilterContextValue {
  filters: FilterOptions;
  setFilters: React.Dispatch<React.SetStateAction<FilterOptions>>;
  filteredQuests: CampaignQuestState[];
  gameGroups: GameGroup[];
  resetFilters: () => void;
  isMobile: boolean;
  setIsMobile: React.Dispatch<React.SetStateAction<boolean>>;
  totalQuestCount: number;
  isLoading: boolean;
  // All available filter options from API
  availablePlatforms: string[];
  availableGenres: string[];
  availableQuestTypes: string[];
  availableRewards: string[];
  availableRecurring: string[];
  // Original quests per game (unfiltered) for progress bar calculation
  originalQuestsByGame: Map<string, CampaignQuestState[]>;
}

const QuestFilterContext = createContext<QuestFilterContextValue | null>(null);

export const defaultFilters: FilterOptions = {
  gameTitle: "",
  platforms: new Set(),
  rewards: new Set(),
  recurring: new Set(),
  questTypes: new Set(),
  genres: new Set(),
  hideCompleted: false,
};

export function QuestFilterProvider({
  children,
  gamesWithQuests,
  gamePriorities,
}: {
  children: ReactNode;
  gamesWithQuests: GameWithQuests[];
  gamePriorities: GamePrioritiesResponse | null;
}) {
  const [filters, setFilters] = useState<FilterOptions>(defaultFilters);
  const [isMobile, setIsMobile] = useState(false);
  const { completedQuests } = useQuestCompletions();
  const getAccessToken = useAccessToken();

  // Fetch predicted stats for weekly points
  const { data: predictedStats } = useSWR<PredictedStatsResponse>(
    "/v3/rewards/sweepstakes/user-stats/predicted",
    (path: string) =>
      authorizedFetcherGet<PredictedStatsResponse>(path, getAccessToken),
  );

  // Map quest externalId to weeklyPoints for quick lookup
  const questWeeklyPointsMap = useMemo(() => {
    if (!predictedStats?.games) return new Map<string, number>();

    const pointsMap = new Map<string, number>();
    for (const game of predictedStats.games) {
      for (const quest of game.quests) {
        if (quest.weeklyPoints !== undefined) {
          pointsMap.set(quest.externalId, quest.weeklyPoints);
        }
      }
    }
    return pointsMap;
  }, [predictedStats]);

  // Feature flag for priority ordering
  const gamePriorityOrderingEnabled = useFlag("gamePriorityOrdering");

  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  // Enrich games with weekly points early so filters can access them
  const enrichedGamesWithQuests = useMemo(() => {
    if (!gamesWithQuests || gamesWithQuests.length === 0) return [];

    return gamesWithQuests.map((game) => ({
      ...game,
      quests: game.quests.map((quest) => ({
        ...quest,
        weeklyPoints: questWeeklyPointsMap.get(quest.externalId) ?? undefined,
      })),
    }));
  }, [gamesWithQuests, questWeeklyPointsMap]);

  // Sort enriched games by priority (only if feature flag is enabled)
  const sortedAndEnrichedGames = useMemo(() => {
    if (!gamePriorityOrderingEnabled || !gamePriorities?.priorities) {
      return enrichedGamesWithQuests;
    }

    // Create priority map with gameplay_order and social_order
    const priorityMap = new Map(
      gamePriorities.priorities.map((p) => [
        p.game_id,
        {
          gamePriority: p.gameplay_order,
          socialPriority: p.social_order,
        },
      ]),
    );

    // Determine which metric to prioritize based on filters
    const isGameplayFiltered = filters.questTypes.has(
      QUEST_TYPE_LABELS.IN_GAME,
    );
    const isSocialFiltered = filters.questTypes.has(QUEST_TYPE_LABELS.SOCIAL);

    // Sort by filter-aware priority
    const sorted = [...enrichedGamesWithQuests].sort((a, b) => {
      const aPriority = priorityMap.get(a.gameId);
      const bPriority = priorityMap.get(b.gameId);

      if (!aPriority && !bPriority) return 0;
      if (!aPriority) return 1;
      if (!bPriority) return -1;

      let orderA: number;
      let orderB: number;

      if (isGameplayFiltered && !isSocialFiltered) {
        // User filtered by "In-game Quests" only → prioritize gameplay order
        orderA = aPriority.gamePriority;
        orderB = bPriority.gamePriority;
      } else if (isSocialFiltered && !isGameplayFiltered) {
        // User filtered by "Social Quests" only → prioritize social order
        orderA = aPriority.socialPriority;
        orderB = bPriority.socialPriority;
      } else {
        // Default (no filter or both): Social first, gameplay as tiebreaker
        orderA = aPriority.socialPriority;
        orderB = bPriority.socialPriority;

        if (orderA === orderB) {
          // Tie on social → use gameplay as tiebreaker
          orderA = aPriority.gamePriority;
          orderB = bPriority.gamePriority;
        }
      }

      return orderA - orderB; // Lower order = higher priority
    });

    return sorted;
  }, [
    enrichedGamesWithQuests,
    gamePriorities,
    gamePriorityOrderingEnabled,
    filters.questTypes,
  ]);

  const {
    availablePlatforms,
    availableGenres,
    availableQuestTypes,
    availableRewards,
    availableRecurring,
  } = useMemo(() => {
    if (!sortedAndEnrichedGames || sortedAndEnrichedGames.length === 0) {
      return {
        availablePlatforms: [] as string[],
        availableGenres: [] as string[],
        availableQuestTypes: [] as string[],
        availableRewards: [] as string[],
        availableRecurring: [] as string[],
      };
    }

    const platformsSet = new Set<string>();
    const genresSet = new Set<string>();
    const questTypesSet = new Set<string>();
    const rewardsSet = new Set<string>();
    const recurringSet = new Set<string>();

    for (const game of sortedAndEnrichedGames) {
      // Extract platforms (platforms is an object with platform keys)
      if (game.platforms) {
        for (const platformKey of Object.keys(game.platforms)) {
          platformsSet.add(getPlatformLabel(platformKey));
        }
      }

      // Extract genres
      for (const genre of game.gameGenres ?? []) {
        genresSet.add(genre);
      }

      // Extract quest data
      for (const quest of game.quests) {
        if (quest.type) {
          if (SOCIAL_QUEST_TYPES.has(quest.type)) {
            questTypesSet.add(QUEST_TYPE_LABELS.SOCIAL);
          } else if (IN_GAME_QUEST_TYPES.has(quest.type)) {
            questTypesSet.add(QUEST_TYPE_LABELS.IN_GAME);
          }
        }

        if (quest.gems > 0) {
          rewardsSet.add(REWARD_TYPES.POINT);
        }
        // Note: Leaderboard points (campaign_point) intentionally excluded from filters
        if (quest.weeklyPoints && quest.weeklyPoints > 0) {
          rewardsSet.add(REWARD_TYPES.WEEKLY_POINT);
        }
        // refreshPeriodHours
        if (
          quest.refreshPeriodHours &&
          RECURRING_PERIODS[quest.refreshPeriodHours]
        ) {
          recurringSet.add(RECURRING_PERIODS[quest.refreshPeriodHours]);
        }
      }
    }

    return {
      availablePlatforms: Array.from(platformsSet).sort(),
      availableGenres: Array.from(genresSet).sort(),
      availableQuestTypes: Array.from(questTypesSet).sort(),
      availableRewards: Array.from(rewardsSet).sort(),
      availableRecurring: Array.from(recurringSet).sort(),
    };
  }, [sortedAndEnrichedGames]);

  // Build a map of original (unfiltered) quests per game for progress bar calculation
  // This ensures the progress bar always shows progress relative to ALL quests, not filtered ones
  const originalQuestsByGame = useMemo(() => {
    const map = new Map<string, CampaignQuestState[]>();
    for (const game of sortedAndEnrichedGames) {
      map.set(game.gameId, game.quests);
    }
    return map;
  }, [sortedAndEnrichedGames]);

  // Group quests by game - build directly from sortedAndEnrichedGames with applied filters
  const gameGroups = useMemo((): GameGroup[] => {
    // First, filter games by platform if needed
    let filteredGames = sortedAndEnrichedGames;

    if (filters.platforms.size > 0) {
      filteredGames = sortedAndEnrichedGames.filter((game) => {
        if (!game.platforms || Object.keys(game.platforms).length === 0)
          return true;

        // Check if any of the game's platforms match the selected filters
        return Object.keys(game.platforms).some((platformKey) => {
          const label = getPlatformLabel(platformKey);
          return filters.platforms.has(label);
        });
      });
    }

    // Apply quest-level filters to each game's quests
    const gameGroupsArray: GameGroup[] = [];

    for (const game of filteredGames) {
      let gameQuests = [...game.quests];

      // Filter by game title
      if (filters.gameTitle.trim()) {
        const searchTerm = filters.gameTitle.toLowerCase();
        gameQuests = gameQuests.filter((quest) => {
          const matchesCaption = quest.caption
            ?.toLowerCase()
            .includes(searchTerm);
          const matchesGameName = game.gameName
            ?.toLowerCase()
            .includes(searchTerm);
          const matchesQuestName = quest.name
            ?.toLowerCase()
            .includes(searchTerm);
          return matchesCaption || matchesGameName || matchesQuestName;
        });
      }

      // Filter by rewards
      if (filters.rewards.size > 0) {
        gameQuests = gameQuests.filter((quest) => {
          // Always show wishlist quests
          if (quest.type === "wishlist") return true;

          if (filters.rewards.has(REWARD_TYPES.POINT) && quest.gems)
            return true;
          if (
            filters.rewards.has(REWARD_TYPES.WEEKLY_POINT) &&
            quest.weeklyPoints
          )
            return true;
          return false;
        });
      }

      // Filter by quest types
      if (filters.questTypes.size > 0) {
        const hasInGameQuest = gameQuests.some((quest) =>
          IN_GAME_QUEST_TYPES.has(quest.type),
        );

        gameQuests = gameQuests.filter((quest) => {
          // Always show wishlist quests
          if (hasInGameQuest && quest.type === "wishlist") return true;

          if (
            filters.questTypes.has(QUEST_TYPE_LABELS.IN_GAME) &&
            IN_GAME_QUEST_TYPES.has(quest.type)
          ) {
            return true;
          }
          if (
            filters.questTypes.has(QUEST_TYPE_LABELS.SOCIAL) &&
            SOCIAL_QUEST_TYPES.has(quest.type)
          ) {
            return true;
          }
          return false;
        });
      }

      // Filter by genres
      if (filters.genres.size > 0) {
        const hasMatchingGenre = game.gameGenres?.some((genre) =>
          filters.genres.has(genre),
        );
        if (!hasMatchingGenre) gameQuests = [];
      }

      // NOTE: hideCompleted filter is NOT applied here intentionally.
      // The progress bar needs access to ALL quests (including completed) to show accurate progress.
      // The hideCompleted filter is applied at the QuestCard render level for the quest list display only.

      // Only add game if it has quests after filtering
      if (gameQuests.length > 0) {
        gameGroupsArray.push({
          gameId: game.gameId,
          gameName: game.gameName,
          gameIcon: game.gameLogo,
          gameSlug: game.gameSlug,
          gameGenres: game.gameGenres || [],
          gameLandingPageUrl: game.gameLandingPageUrl,
          quests: gameQuests,
          featured: FEATURED_GAMES.includes(game.gameName),
        });
      }
    }

    // Sort games by number of quests (descending)
    return gameGroupsArray;
  }, [sortedAndEnrichedGames, filters]);

  // Flatten gameGroups to get all filtered quests (already enriched with weekly points)
  const filteredQuests = useMemo(
    () => gameGroups.flatMap((group) => group.quests),
    [gameGroups],
  );

  return (
    <QuestFilterContext.Provider
      value={{
        filters,
        setFilters,
        filteredQuests,
        gameGroups,
        resetFilters,
        isMobile,
        setIsMobile,
        totalQuestCount: filteredQuests.length,
        isLoading: false,
        availablePlatforms,
        availableGenres,
        availableQuestTypes,
        availableRewards,
        availableRecurring,
        originalQuestsByGame,
      }}
    >
      {children}
    </QuestFilterContext.Provider>
  );
}

export function useQuestFilter() {
  const context = useContext(QuestFilterContext);
  if (!context) {
    throw new Error("useQuestFilter must be used within QuestFilterProvider");
  }
  return context;
}
